// ============================================================================
// The stock half of an order's page: where each line ships, what it costs
// landed, and what actually arrived. Rendered by po-detail ONLY for an order
// with lines shipping to a stock location — a normal building order shows
// nothing new.
//
// One component per write (§15a):
//   * ReceiveLineWindow   — stock_receive_line (confirm what arrived)
//   * EditOrderWindow     — the order's freight, tax, dates, number
//   * EditLineWindow      — a line before it is received
//   * CorrectEntryWindow  — the received quantity, after it is received
// A shortfall is FLAGGED against the order, never absorbed.
// ============================================================================

import { useEffect, useState } from 'react';
import {
  fetchStockOffices,
  fetchStockPoLines,
  receiveLine,
  updatePurchase,
  updatePurchaseLine,
  type PoLineRow,
} from './api';
import { fmtDay, fmtMoney, fmtQty } from './format';
import { useStockLoad } from './use-stock-data';
import { FIELD_CLASS, QtyField, StockWindow, parseQty } from './stock-window';
import { CorrectEntryWindow, type CorrectionTarget } from './correct-entry-window';

const BTN =
  'rounded border border-tcs-border bg-tcs-raised px-2.5 py-1 text-xs font-medium text-tcs-text hover:bg-tcs-surface';

const unitOf = (l: PoLineRow) => (l.supply_id === 'SUP-0230' ? 'tabs' : l.supply_id === 'SUP-0212' ? 'gal' : 'each');

export function StockReceivingSection({
  poId,
  po,
}: {
  poId: string;
  po: { shipping: number | null; tax: number | null; expected_delivery: string | null; invoice_number: string | null } | null;
}) {
  const linesQ = useStockLoad(() => fetchStockPoLines(poId), [poId], [] as PoLineRow[]);
  const officesQ = useStockLoad(() => fetchStockOffices(), [], [] as Awaited<ReturnType<typeof fetchStockOffices>>);
  const [receive, setReceive] = useState<PoLineRow | null>(null);
  const [editLine, setEditLine] = useState<PoLineRow | null>(null);
  const [editOrder, setEditOrder] = useState(false);
  const [correction, setCorrection] = useState<CorrectionTarget | null>(null);

  const lines = linesQ.data;
  if (linesQ.loading || lines.length === 0) return null;
  const officeName = (id: string | null) => officesQ.data.find((o) => o.office_id === id)?.office_name ?? id ?? '—';
  const short = lines.filter((l) => Number(l.short_units ?? 0) > 0);

  return (
    <section className="mt-6 rounded-xl border border-tcs-border bg-tcs-bg p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-tcs-text">Stock — where it went and what arrived</h2>
        <button type="button" className={BTN} onClick={() => setEditOrder(true)}>
          Edit freight, tax and dates
        </button>
      </div>
      <p className="text-xs text-tcs-text-muted">
        Landed cost spreads freight ({fmtMoney(po?.shipping ?? 0)}) and tax ({fmtMoney(po?.tax ?? 0)}) across the lines by line
        value. Change either and every cost downstream recalculates.
      </p>
      {short.length > 0 && (
        <p role="alert" className="mt-2 rounded border border-st-warn-border bg-st-warn-bg px-3 py-2 text-xs text-st-warn-fg-strong">
          Short on this order:{' '}
          {short.map((l) => `${fmtQty(l.short_units, unitOf(l))} of ${l.item_name} at ${officeName(l.office_id)}`).join('; ')}. Take it up with
          the vendor — the missing stock was not added.
        </p>
      )}
      <ul className="mt-3 divide-y divide-tcs-border">
        {lines.map((l) => {
          const u = unitOf(l);
          const received = l.received_units != null;
          return (
            <li key={l.line_id} className="flex flex-wrap items-start justify-between gap-2 py-2.5">
              <div className="min-w-0 text-sm">
                <p className="font-medium text-tcs-text">
                  {Number(l.cases)} case{Number(l.cases) === 1 ? '' : 's'} × {l.item_name}
                </p>
                <p className="text-xs text-tcs-text-secondary">
                  Ship to {officeName(l.office_id)} · {fmtQty(l.ordered_units, u)} ordered · {fmtMoney(l.line_value)}
                  {l.landed_unit_cost != null ? ` · landed ${fmtMoney(l.landed_unit_cost, u === 'tabs' ? 3 : 2)} per ${u === 'tabs' ? 'tab' : u === 'gal' ? 'gallon' : 'each'}` : ''}
                </p>
                <p className="text-xs text-tcs-text-muted">
                  {received
                    ? `Received ${fmtQty(l.received_units, u)} on ${fmtDay(l.received_at)}`
                    : l.po_status === 'Draft'
                      ? 'Draft — not ordered yet'
                      : 'Waiting'}
                </p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {!received && (
                  <>
                    <button type="button" className={BTN} onClick={() => setEditLine(l)}>
                      Edit line
                    </button>
                    <button type="button" className={BTN} onClick={() => setReceive(l)}>
                      Confirm what arrived
                    </button>
                  </>
                )}
                {received && l.receipt_id && (
                  <button
                    type="button"
                    className={BTN}
                    onClick={() =>
                      setCorrection({
                        movementId: l.receipt_id ?? '',
                        what: `what arrived on ${fmtDay(l.received_at)}`,
                        currentQty: Number(l.received_units ?? 0),
                        unitLabel: u,
                      })
                    }
                  >
                    Correct received quantity
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      <ReceiveLineWindow open={!!receive} onClose={() => setReceive(null)} line={receive} officeName={officeName} />
      <EditLineWindow open={!!editLine} onClose={() => setEditLine(null)} line={editLine} offices={officesQ.data} />
      <EditOrderWindow open={editOrder} onClose={() => setEditOrder(false)} poId={poId} po={po} lines={lines} />
      <CorrectEntryWindow open={!!correction} onClose={() => setCorrection(null)} target={correction} />
    </section>
  );
}

function ReceiveLineWindow({
  open,
  onClose,
  line,
  officeName,
}: {
  open: boolean;
  onClose: () => void;
  line: PoLineRow | null;
  officeName: (id: string | null) => string;
}) {
  const [cases, setCases] = useState('');
  const [on, setOn] = useState('');
  useEffect(() => {
    if (open && line) {
      setCases(String(line.cases ?? ''));
      // The local calendar day, not UTC (after 7 pm Central the UTC date is tomorrow).
      setOn(new Date().toLocaleDateString('en-CA'));
    }
  }, [open, line]);
  if (!line) return null;
  const n = parseQty(cases);
  return (
    <StockWindow
      open={open}
      title="Confirm what arrived"
      subtitle={`${line.item_name} at ${officeName(line.office_id)} — ${Number(line.cases)} case(s) ordered`}
      canSave={n != null && !!on}
      saveLabel="Confirm"
      onCancel={onClose}
      onSave={async () => {
        await receiveLine({ lineId: line.line_id ?? '', cases: n ?? 0, receivedOn: on });
        onClose();
      }}
    >
      <QtyField label="Cases that actually arrived" value={cases} onChange={setCases} step={1} hint="Fewer than ordered is flagged against the order." />
      <label className="block">
        <span className="mb-1 block text-xs text-tcs-text-muted">Arrived on</span>
        <input type="date" className={FIELD_CLASS} value={on} onChange={(e) => setOn(e.target.value)} />
      </label>
    </StockWindow>
  );
}

function EditLineWindow({
  open,
  onClose,
  line,
  offices,
}: {
  open: boolean;
  onClose: () => void;
  line: PoLineRow | null;
  offices: Awaited<ReturnType<typeof fetchStockOffices>>;
}) {
  const [cases, setCases] = useState('');
  const [price, setPrice] = useState('');
  const [office, setOffice] = useState('');
  useEffect(() => {
    if (open && line) {
      setCases(String(line.cases ?? ''));
      setPrice(String(line.case_price ?? ''));
      setOffice(line.office_id ?? '');
    }
  }, [open, line]);
  if (!line) return null;
  const n = parseQty(cases);
  const p = parseQty(price);
  return (
    <StockWindow
      open={open}
      title="Edit order line"
      subtitle={line.item_name ?? ''}
      canSave={n != null && n > 0 && p != null && !!office}
      onCancel={onClose}
      onSave={async () => {
        await updatePurchaseLine(line.line_id ?? '', {
          qty: n,
          unit_price: p,
          extended_price: (n ?? 0) * (p ?? 0),
          surplus_storage_location_id: office,
        });
        onClose();
      }}
    >
      <QtyField label="Cases" value={cases} onChange={setCases} step={1} />
      <label className="block">
        <span className="mb-1 block text-xs text-tcs-text-muted">Case price</span>
        <input inputMode="decimal" className={FIELD_CLASS} value={price} onChange={(e) => setPrice(e.target.value)} />
      </label>
      <label className="block">
        <span className="mb-1 block text-xs text-tcs-text-muted">Ship to stock location</span>
        <select className={FIELD_CLASS} value={office} onChange={(e) => setOffice(e.target.value)}>
          {offices.map((o) => (
            <option key={o.office_id} value={o.office_id}>
              {o.office_name}
            </option>
          ))}
        </select>
      </label>
    </StockWindow>
  );
}

function EditOrderWindow({
  open,
  onClose,
  poId,
  po,
  lines,
}: {
  open: boolean;
  onClose: () => void;
  poId: string;
  po: { shipping: number | null; tax: number | null; expected_delivery: string | null; invoice_number: string | null } | null;
  lines: PoLineRow[];
}) {
  const [d, setD] = useState({ shipping: '', tax: '', expected: '', invoice: '' });
  useEffect(() => {
    if (open)
      setD({
        shipping: String(po?.shipping ?? ''),
        tax: String(po?.tax ?? ''),
        expected: po?.expected_delivery ?? '',
        invoice: po?.invoice_number ?? '',
      });
  }, [open, po]);
  const subtotal = lines.reduce((s, l) => s + Number(l.line_value ?? 0), 0);
  return (
    <StockWindow
      open={open}
      title="Edit the order"
      canSave
      onCancel={onClose}
      onSave={async () => {
        const shipping = parseQty(d.shipping) ?? 0;
        const tax = parseQty(d.tax) ?? 0;
        await updatePurchase(poId, {
          shipping,
          tax,
          subtotal,
          total: subtotal + shipping + tax,
          expected_delivery: d.expected || null,
          invoice_number: d.invoice || null,
        });
        onClose();
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        {(
          [
            ['shipping', 'Freight'],
            ['tax', 'Tax (use the final invoice)'],
          ] as const
        ).map(([k, label]) => (
          <label key={k} className="block">
            <span className="mb-1 block text-xs text-tcs-text-muted">{label}</span>
            <input inputMode="decimal" className={FIELD_CLASS} value={d[k]} onChange={(e) => setD((x) => ({ ...x, [k]: e.target.value }))} />
          </label>
        ))}
        <label className="block">
          <span className="mb-1 block text-xs text-tcs-text-muted">Expected arrival</span>
          <input type="date" className={FIELD_CLASS} value={d.expected} onChange={(e) => setD((x) => ({ ...x, expected: e.target.value }))} />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs text-tcs-text-muted">Quote, order or invoice number</span>
          <input className={FIELD_CLASS} value={d.invoice} onChange={(e) => setD((x) => ({ ...x, invoice: e.target.value }))} />
        </label>
      </div>
    </StockWindow>
  );
}
